/* eslint-disable functional/prefer-readonly-type */
import * as Apollo from "@apollo/client";
import { ReadonlyArray } from "@morphism/fp";
import * as Ant from "antd";
import React from "react";
import styled from "styled-components";

import { DVIRs } from "~/DVIR";
import { Graph } from "~/Graph";
import * as Layout from "~/Layout";
import * as Loading from "~/Loading";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

import { Summary } from "./Summary";
import { Status } from "./Table/Status";

export function Calendar({
  interval,
  summaries,
  loading,
  errors,
  onNavigate,
}: {
  readonly interval: Time.Interval.Stopped;
  readonly summaries: Summary.Summaries;
  readonly loading: boolean;
  readonly errors?: Apollo.ApolloError;
  readonly onNavigate: (
    dateTime: Time.DateTime & { readonly status?: Status.StatusEnum[] }
  ) => void;
}) {
  const days = React.useMemo(
    () =>
      pipe(
        summaries,
        (summaries) => [...summaries],
        ReadonlyArray.reduce(
          {} as Record<string, DVIRs.Statistics>,
          (previous, current) =>
            pipe(current, Time.DateTime.toStartOfDay, ({ dateTime }) => ({
              ...previous,
              [dateTime]: pipe(current, DVIRs.Statistics.fromSummary),
            }))
        )
      ),
    [interval, summaries.length]
  );

  const dateCellRender: React.ComponentProps<
    typeof Ant.Calendar
  >["dateCellRender"] = (moment) => {
    const dateTime = { dateTime: moment.valueOf() };
    return pipe(
      dateTime,
      Time.DateTime.toStartOfDay,
      ({ dateTime }) => days[dateTime] ?? DVIRs.Statistics.empty(),
      ({ count, defects: { resolved, unresolved, none } }) =>
        count > 0 && (
          <>
            <Ant.Tag onClick={() => onNavigate(dateTime)}>
              {count} DVIRs
            </Ant.Tag>
            {resolved > 0 && (
              <Ant.Tag
                color="green"
                onClick={() => onNavigate({ ...dateTime, status: [Status.StatusEnum.RESOLVED] })}
              >
                {resolved} resolved defects
              </Ant.Tag>
            )}
            {none > 0 && (
              <Ant.Tag
                color="green"
                onClick={() => onNavigate({ ...dateTime, status: [Status.StatusEnum.NODEFECT] })}
              >
                {none} no defects
              </Ant.Tag>
            )}
            {unresolved > 0 && (
              <Ant.Tag
                color="red"
                onClick={() => onNavigate({ ...dateTime, status: [Status.StatusEnum.UNRESOLVED] })}
              >
                {unresolved} unresolved defects
              </Ant.Tag>
            )}
          </>
        )
    );
  };

  return (
    <Calendar.Container left bottom right>
      <Ant.Calendar mode="month" dateCellRender={dateCellRender} />
      {loading && (
        <Calendar.Loading>
          <Typography.H2.Component>
            <Loading.Component />
          </Typography.H2.Component>
        </Calendar.Loading>
      )}
      {errors
        ? Graph.Errors.renderApolloError(
            "Error while retrieving DVIR Summaries",
            errors
          )
        : null}
    </Calendar.Container>
  );
}

export namespace Calendar {
  export const Container = styled(Layout.Block.Component)`
    position: relative;

    .ant-picker-calendar-date-value {
      position: absolute;
      top: 4px;
      right: 4px;
      font-weight: 900;
    }

    .ant-picker-calendar-full
      .ant-picker-panel
      .ant-picker-calendar-date-content {
      height: 130px;
    }

    .ant-picker-calendar-mode-switch {
      display: none;
    }

    .ant-picker-cell {
      cursor: default;
    }

    .ant-tag {
      cursor: pointer;
    }
  `;

  export const Loading = styled.div`
    z-index: 10;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    opacity: 0.8;
    background: white;

    text-align: center;
  `;
}
